import { formatCurrency, formatPersonDateForDisplay } from "@/shared/utils/formatters";
import { buildAddress } from "@/shared/utils/addressUtils";
import type { AddressParts } from "@/shared/utils/addressUtils";
import type { EstateItem } from "@/shared/types/estateTypes";

const SEP = " \u00b7 ";

function meta(item: EstateItem): Record<string, unknown> {
  return (item.metadata ?? {}) as Record<string, unknown>;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === "number" && value ? value : undefined;
}

function buildInsuranceSubtitle(item: EstateItem): string {
  const m = meta(item);
  const parts: string[] = [];
  if (item.item_type) parts.push(item.item_type);
  if (str(m.policy_number)) parts.push(`Policy #${m.policy_number}`);
  if (item.balance) parts.push(formatCurrency(item.balance));
  return parts.join(SEP);
}

function buildLtcSubtitle(item: EstateItem): string {
  const m = meta(item);
  const parts: string[] = [];
  if (str(m.policy_number)) parts.push(`Policy #${m.policy_number}`);
  if (item.balance) parts.push(`${formatCurrency(item.balance)}/day`);
  if (str(m.benefit_period)) parts.push(m.benefit_period as string);
  return parts.join(SEP);
}

function buildDebtSubtitle(item: EstateItem): string {
  const m = meta(item);
  const parts: string[] = [];
  if (str(m.lender_name)) parts.push(m.lender_name as string);
  if (item.balance) parts.push(formatCurrency(item.balance));
  if (num(m.monthly_payment)) parts.push(`${formatCurrency(m.monthly_payment as number)}/mo`);
  return parts.join(SEP);
}

function buildExpenseSubtitle(item: EstateItem): string {
  const m = meta(item);
  const parts: string[] = [];

  switch (item.item_type) {
    case "Funeral Home Cost":
      if (item.balance) parts.push(formatCurrency(item.balance));
      break;
    case "Storage units":
      if (str(m.unit_number)) parts.push(`Unit ${m.unit_number}`);
      if (str(m.address)) parts.push(m.address as string);
      break;
    case "Subscriptions":
      if (item.balance) parts.push(`${formatCurrency(item.balance)}/mo`);
      break;
    case "Professional and service fees":
      if (str(m.payee)) parts.push(m.payee as string);
      if (item.balance) parts.push(formatCurrency(item.balance));
      if (str(m.paid_on)) parts.push(`Paid ${formatPersonDateForDisplay(m.paid_on as string)}`);
      break;
    default:
      if (str(m.cost_type)) parts.push(m.cost_type as string);
      if (item.balance) parts.push(`${formatCurrency(item.balance)}/mo`);
      break;
  }

  return parts.join(SEP);
}

function buildPropertySubtitle(item: EstateItem): string {
  const m = meta(item);
  const parts: string[] = [];
  const propertyType =
    item.item_type && item.item_type !== "Properties"
      ? item.item_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : undefined;
  if (propertyType) parts.push(propertyType);
  if (str(m.value_source) === "smart_estimator") {
    parts.push("Zillow estimate");
  }
  if (item.balance) parts.push(formatCurrency(item.balance));
  return parts.join(SEP);
}

const FINANCIAL_ACCOUNT_CATEGORIES = new Set([
  "Bank accounts",
  "Brokerage accounts",
  "Tax-advantaged savings",
  "Retirement accounts",
  "Crypto accounts",
  "NFTs",
  "Other digital assets",
]);

function buildFinancialAccountSubtitle(item: EstateItem): string {
  const m = meta(item);
  const parts: string[] = [];
  const rawLast4 = str(item.identifiers?.account_last4) ?? str(m.account_last4);
  const last4 = rawLast4?.replace(/\D/g, "").slice(-4);
  if (last4) parts.push(`\u2022\u2022\u2022\u2022${last4}`);
  if (item.item_type) parts.push(item.item_type);
  if (item.balance) parts.push(formatCurrency(item.balance));
  return parts.join(SEP);
}

function buildSafeDepositSubtitle(item: EstateItem): string {
  const m = meta(item);
  const parts: string[] = [];
  if (item.item_type) parts.push(item.item_type);
  const address = m.address as AddressParts | undefined;
  if (address) {
    const formatted = buildAddress(address).trim();
    if (formatted) parts.push(formatted);
  }
  const boxNumber = str(m.box_number);
  if (boxNumber) parts.push(`Box #${boxNumber}`);
  return parts.join(SEP);
}

function buildDefaultSubtitle(item: EstateItem): string {
  const parts: string[] = [];
  if (item.item_type) parts.push(item.item_type);
  if (item.balance) parts.push(formatCurrency(item.balance));
  return parts.join(SEP);
}

export function buildItemSubtitle(item: EstateItem): string {
  switch (item.category) {
    case "Properties":
      return buildPropertySubtitle(item);
    case "Life insurance":
      return buildInsuranceSubtitle(item);
    case "Long-term care insurance":
      return buildLtcSubtitle(item);
    case "Annuity contracts":
      return buildInsuranceSubtitle(item);
    case "Secured Debts":
      return buildDebtSubtitle(item);
    case "Unsecured Debts":
      return buildDebtSubtitle(item);
    case "Ongoing Estate Expenses":
      return buildExpenseSubtitle(item);
    case "Safe or safety deposit box":
      return buildSafeDepositSubtitle(item);
    default:
      if (FINANCIAL_ACCOUNT_CATEGORIES.has(item.category)) {
        return buildFinancialAccountSubtitle(item);
      }
      return buildDefaultSubtitle(item);
  }
}
